import { SEO_TITLE_STROLLER_REVIEWS_stroller, STROLLER_SEO_GENERAL_DESC } from 'mk/autogenerated/translations/StrollerProductPhotosGalleryPage.36ef319650d14948ff070638e6e4f5ad'
import {
    strollerProductPhotoFetchTrigger,
    strollerProductPhotoToggleThumbsTrigger,
} from 'mk2/apps/strollers/containers/StrollerProductPhoto/StrollerProductPhoto.actions';
import {
    strollersProductPhotoReducer,
} from 'mk2/apps/strollers/containers/StrollerProductPhoto/StrollerProductPhoto.reducers';
import strollersProductPhotoSaga
    from 'mk2/apps/strollers/containers/StrollerProductPhoto/StrollerProductPhoto.sagas';
import {
    getStrollerProductPhotoPagesState,
    getStrollerProductPhotoState,
} from 'mk2/apps/strollers/containers/StrollerProductPhoto/StrollerProductPhoto.selectors';
import {
    StrollerProductPhotosGallery,
} from 'mk2/apps/strollers/containers/StrollerProductPhotosGallery/StrollerProductPhotosGallery';
import { strollersDetailUrl, strollersDetailVariantUrl } from 'mk2/apps/strollers/urls';
import { AppName } from 'mk2/containers/Application/Application';
import { cacheLast } from 'mk2/helpers/cache';
import { resizeForCover } from 'mk2/helpers/photos';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { url } from 'mk2/helpers/urls';
import Page, { HalfpageProps, RightContentSide } from 'mk2/pages/Page';
import { AppState } from 'mk2/reducers';
import { LoadingState } from 'mk2/reducers/loadingState';
import {
    PhotoDetailSchema,
    StrollerEntity,
    StrollerPhoto,
    StrollerSchema,
    StrollerVariantEntity,
    StrollerVariantSchema,
} from 'mk2/schemas';
import { getDenormalizedEntities, getDenormalizedEntity } from 'mk2/selectors';
import { interpolate } from 'mk2/services/i18n';
import { Store } from 'mk2/store/configureStore';
import React from 'react';
import { ReactImageGalleryItem } from 'react-image-gallery';
import { MapStateToPropsParam } from 'react-redux';
import { RouteComponentProps } from 'react-router';

interface ImageGalleryItem extends ReactImageGalleryItem {
    id: number;
    fullSizeUrl?: string;
    width: number;
    height: number;
}

export interface OwnProps {}

export interface StateProps {
    strollerSlug: string;
    strollerVariantId: number | null;
    loadingState: LoadingState;
    stroller: StrollerEntity;
    strollerPhotos: StrollerPhoto[];
    strollerVariants: StrollerVariantEntity[];
    images: ImageGalleryItem[];
}

export interface DispatchProps {
    onLoad(slug: string, strollerVariantId?: number);
    onToggleThumbnails();
}

export type RouteProps = RouteComponentProps<{
    slug: string;
    variantId: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class StrollerProductPhotosGalleryPage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {

    private imagesCache = cacheLast<ImageGalleryItem[]>(true);

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const slug = ownProps.match.params.slug;
            const strollerVariantId = !!ownProps.match.params.variantId ? Number(ownProps.match.params.variantId) : null;

            const pagesState = getStrollerProductPhotoPagesState(state);
            const photosState = getStrollerProductPhotoState(state, slug);
            const stroller = getDenormalizedEntity<StrollerEntity>(state, StrollerSchema, photosState.strollerId);
            const strollerPhotos = getDenormalizedEntities<StrollerPhoto>(state, PhotoDetailSchema, photosState.strollerPhotosIds);
            const strollerVariants = getDenormalizedEntities<StrollerVariantEntity>(state, StrollerVariantSchema, photosState.strollerVariantIds);

            const hideThumbnails = pagesState.hideThumbnails;

            const images = this.imagesCache(() => {
                const galleryPhotos: ImageGalleryItem[] = [];

                strollerPhotos.map((p) => {
                    const thumbData = !hideThumbnails ? {
                        thumbnail: p.url250x250,
                        thumbnailHeight: resizeForCover(p, 42).height,
                        thumbnailWidth: resizeForCover(p, 42).width,
                    } : {};
                    galleryPhotos.push({
                        id: p.id,
                        ...thumbData,
                        original: p.url720x720,
                        width: p.originalWidth,
                        height: p.originalHeight,
                    });
                });

                return galleryPhotos;
            }, stroller, strollerPhotos);

            return {
                strollerSlug: slug,
                strollerVariantId,
                loadingState: photosState.loadingState,
                stroller,
                strollerPhotos,
                strollerVariants,
                images,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: strollerProductPhotoFetchTrigger,
            onToggleThumbnails: strollerProductPhotoToggleThumbsTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const { stroller } = props;
        return stroller ? interpolate(SEO_TITLE_STROLLER_REVIEWS_stroller, { stroller: stroller.name }) : null;
    }

    public getMetaDescription(props: Props): string {
        const { stroller } = props;
        if (stroller) {
            return `${stroller.name} - ${STROLLER_SEO_GENERAL_DESC}`;
        }
    }

    public render(props: Props): false | JSX.Element {
        return <StrollerProductPhotosGallery {...props} />;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasMobileLogo(props: Props): boolean {
        return true;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        const { strollerSlug, strollerVariantId } = props;
        return !!strollerVariantId ? url(strollersDetailVariantUrl, { slug: strollerSlug, variantId: strollerVariantId })
                        : url(strollersDetailUrl, { slug: strollerSlug });
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: 'Strollers Index - Halfpage Sticky (Desktop)',
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer('containers.strollers.strollersProductPhoto', strollersProductPhotoReducer);
        store.injectSaga('containers.strollers.strollersProductPhoto', strollersProductPhotoSaga);
    }
}
